<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-clock"></i>
          ລາຍການເຕີມເງິນລໍຖ້າ
          <span class="countTotal">({{ pagination.total }})</span>
        </h4>
        <vs-button flat size="small" @click="fetchPendingTopups" :loading="isLoading">
          <i class="fal fa-sync-alt"></i> ໂຫລດໃໝ່
        </vs-button>
      </div>

      <!-- Filters -->
      <div class="box">
        <div class="columns">
          <div class="column is-3">
            <div class="control">
              <label class="label">ສາຂາ</label>
              <BranchSelect v-model="filters.branch_id" placeholder="ທັງໝົດ" @input="onFilterChange" />
            </div>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div class="box">
        <div v-if="isLoading" class="has-text-centered py-5">
          <i class="fal fa-spinner fa-spin fa-2x"></i>
          <p class="mt-3">ກຳລັງໂຫລດ...</p>
        </div>

        <div v-else-if="error" class="notification is-warning">
          <p>{{ error }}</p>
          <p v-if="errorRequestId" class="error-ref">ref: {{ errorRequestId }}</p>
          <vs-button @click="fetchPendingTopups" class="mt-3">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>

        <div v-else-if="topups.length === 0" class="has-text-centered py-5">
          <i class="fal fa-check-circle fa-3x has-text-grey-light"></i>
          <p class="mt-3 has-text-grey">ບໍ່ມີລາຍການລໍຖ້າ</p>
        </div>

        <div v-else>
          <table class="table is-fullwidth is-hoverable is-striped">
            <thead>
              <tr>
                <th>ID</th>
                <th>ສາຂາ</th>
                <th>ປະເພດກະເປົ໋າ</th>
                <th>ຈຳນວນ</th>
                <th>ທະນາຄານ</th>
                <th>ສະຖານະ</th>
                <th>ວັນທີ</th>
                <th>ຂໍ້ຜິດພາດ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="topup in topups" :key="topup.id">
                <td><small class="has-text-grey">#{{ topup.id }}</small></td>
                <td>
                  <strong v-if="topup.branch_name">{{ topup.branch_name }}</strong>
                  <span v-else>Branch #{{ topup.branch_id }}</span>
                </td>
                <td>
                  <span :class="['tag', topup.wallet_type === 'FREIGHT' ? 'is-info' : 'is-warning']">
                    {{ topup.wallet_type }}
                  </span>
                </td>
                <td><strong>{{ formatCurrency(topup.amount) }}</strong></td>
                <td>
                  <span class="tag is-light">{{ topup.bank_provider || '-' }}</span>
                </td>
                <td>
                  <span :class="['tag', getStatusClass(topup.status)]">
                    {{ formatStatus(topup.status) }}
                  </span>
                </td>
                <td><small>{{ formatDate(topup.created_at) }}</small></td>
                <td>
                  <small v-if="topup.error_message" class="has-text-danger" :title="topup.error_message">
                    {{ truncate(topup.error_message, 30) }}
                  </small>
                  <span v-else class="has-text-grey">-</span>
                </td>
                <td>
                  <!-- Resolve credits the wallet (like a top-up): SuperAdmin/accountingmanager only.
                       UI-only gate — the Go 'admin' JWT still authorizes resolve (not enforced at Go). -->
                  <vs-button v-if="$can(['SuperAdmin', 'accountingmanager'])" flat size="small" @click="openResolveDialog(topup)">
                    <i class="fal fa-wrench"></i> ແກ້ໄຂ
                  </vs-button>
                  <span v-else class="has-text-grey">-</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Inflow-only money total per wallet (degrades to nothing when `totals` absent). -->
          <WalletListTotals :totals="totals" mode="inflow" />

          <!-- Total count -->
          <div class="has-text-right py-2">
            <small class="has-text-grey">ທັງໝົດ {{ pagination.total }} ລາຍການ</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Resolve Dialog -->
    <vs-dialog v-model="showResolveDialog" width="500px" prevent-close @close="closeResolveDialog">
      <template #header>
        <h4 class="dialog-title">
          <i class="fal fa-wrench"></i> ແກ້ໄຂການເຕີມເງິນ #{{ resolveTarget?.id }}
        </h4>
      </template>

      <div v-if="resolveTarget" class="dialog-content">
        <div class="info-row">
          <span class="info-label">ສາຂາ:</span>
          <span class="info-value">{{ resolveTarget.branch_name || `Branch #${resolveTarget.branch_id}` }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ຈຳນວນ:</span>
          <span class="info-value"><strong>{{ formatCurrency(resolveTarget.amount) }}</strong></span>
        </div>
        <div class="info-row">
          <span class="info-label">ປະເພດກະເປົ໋າ:</span>
          <span class="info-value">{{ resolveTarget.wallet_type }}</span>
        </div>

        <hr>

        <div class="form-group">
          <label class="label">ເຫດຜົນ <span class="required">*</span></label>
          <textarea
            class="textarea"
            v-model="resolveForm.reason"
            rows="3"
            placeholder="ລະບຸເຫດຜົນໃນການແກ້ໄຂ..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="label">ສະລິບ/ເອກະສານ <span class="required">*</span></label>
          <div class="file-upload">
            <input
              type="file"
              ref="slipInput"
              accept="image/*,.pdf"
              @change="handleSlipSelect"
              style="display: none"
            >
            <vs-button flat @click="$refs.slipInput.click()" :disabled="isResolving">
              <i class="fal fa-upload"></i> ເລືອກໄຟລ໌
            </vs-button>
            <span v-if="resolveForm.slipFile" class="file-name">
              {{ resolveForm.slipFile.name }}
              <i class="fal fa-times" @click="clearSlip" style="cursor: pointer; margin-left: 8px;"></i>
            </span>
          </div>
          <p class="help-text">ຮອງຮັບ: JPG, PNG, PDF (ສູງສຸດ 5MB)</p>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <vs-button flat @click="closeResolveDialog" :disabled="isResolving">
            ຍົກເລີກ
          </vs-button>
          <vs-button
            @click="submitResolve"
            :loading="isResolving"
            :disabled="!isResolveFormValid || isResolving"
          >
            <i class="fal fa-check"></i> ແກ້ໄຂ
          </vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatCurrency, formatErrorMessage, formatDate, truncate } from '@/services/walletFormatters';
import WalletListTotals from './WalletListTotals.vue';
import BranchSelect from '@/components/BranchSelect.vue';

export default {
  name: 'PendingTopups',
  components: { WalletListTotals, BranchSelect },
  data() {
    return {
      topups: [],
      totals: null,
      isLoading: false,
      error: null,
      errorRequestId: null,
      abortController: null,
      filters: {
        branch_id: '',
      },
      pagination: {
        total: 0,
      },
      showResolveDialog: false,
      resolveTarget: null,
      // [B1] Stable idempotency key for the current resolve-intent — set once when the dialog opens
      // and reused across resubmits, so a retried resolve after a timeout is deduped by Go.
      resolveIdempotencyKey: null,
      isResolving: false,
      resolveForm: {
        reason: '',
        slipFile: null,
      },
    };
  },
  computed: {
    isResolveFormValid() {
      return this.resolveForm.reason.trim().length > 0 && this.resolveForm.slipFile !== null;
    },
  },
  mounted() {
    this.fetchPendingTopups();
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    onFilterChange() {
      this.fetchPendingTopups();
    },

    async fetchPendingTopups() {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.isLoading = true;
      this.error = null;
      this.errorRequestId = null;

      try {
        const params = {};
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;

        const response = await walletService.listPendingTopups(
          this.$axios, params, this.abortController.signal
        );
        this.topups = response.data?.items || response.data || [];
        this.pagination.total = response.data?.total || response.pagination?.total || response.meta?.total || 0;
        // Inflow-only pending total (out is always 0, net == in). No pagination here → refresh every fetch.
        this.totals = response.data?.totals || response.totals || null;
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        const { message, requestId } = extractErrorInfo(error);
        this.error = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້';
        this.errorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },

    openResolveDialog(topup) {
      this.resolveTarget = topup;
      this.resolveForm = { reason: '', slipFile: null };
      // [B1] One key per resolve-intent (per dialog open), reused across resubmits.
      this.resolveIdempotencyKey = `resolve-topup-${crypto.randomUUID()}`;
      this.showResolveDialog = true;
    },

    closeResolveDialog() {
      this.showResolveDialog = false;
      this.resolveTarget = null;
    },

    handleSlipSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate MIME type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ປະເພດໄຟລ໌ບໍ່ຖືກຕ້ອງ (ຮອງຮັບ: JPG, PNG, GIF, PDF)');
        event.target.value = '';
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ໄຟລ໌ໃຫຍ່ເກີນໄປ (ສູງສຸດ 5MB)');
        return;
      }

      this.resolveForm.slipFile = file;
    },

    clearSlip() {
      this.resolveForm.slipFile = null;
      if (this.$refs.slipInput) {
        this.$refs.slipInput.value = '';
      }
    },

    async submitResolve() {
      if (!this.isResolveFormValid || !this.resolveTarget) return;

      const t = this.resolveTarget;
      const label = t.branch_name || `Branch #${t.branch_id}`;
      const confirmed = confirm(
        `ຢືນຢັນແກ້ໄຂການເຕີມເງິນ?\n\nສາຂາ: ${label}\nປະເພດ: ${t.wallet_type}\nຈຳນວນ: ${formatCurrency(t.amount)}`
      );
      if (!confirmed) return;

      this.isResolving = true;
      try {
        const response = await walletService.resolveTopup(
          this.$axios,
          this.resolveTarget.id,
          this.resolveForm.reason,
          this.resolveForm.slipFile,
          this.resolveIdempotencyKey // [B1] stable per-intent key (reused across resubmits)
        );

        const data = response?.data || response;
        if (data?.is_duplicate) {
          this.$notification.OpenNotification_Error('top-right', 'warning', 5000, 'ລາຍການນີ້ຖືກດຳເນີນການແລ້ວ');
        } else {
          this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
        }

        this.closeResolveDialog();
        this.fetchPendingTopups();
      } catch (error) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'ບໍ່ສາມາດແກ້ໄຂໄດ້'));
      } finally {
        this.isResolving = false;
      }
    },

    formatCurrency(amount) {
      return formatCurrency(amount);
    },

    formatDate,

    formatStatus(status) {
      const labels = {
        PENDING: 'ລໍຖ້າ',
        FAILED: 'ລົ້ມເຫລວ',
        PROCESSING: 'ກຳລັງດຳເນີນ',
      };
      return labels[status] || status;
    },

    getStatusClass(status) {
      const classes = {
        PENDING: 'is-warning',
        FAILED: 'is-danger',
        PROCESSING: 'is-info',
      };
      return classes[status] || 'is-light';
    },

    truncate,
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 20px;
}

.box-title {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    font-size: 18px;
    font-weight: 600;

    i {
      margin-right: 8px;
      color: #f0ad4e;
    }
  }
}

.countTotal {
  color: #666;
  font-weight: normal;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mt-3 {
  margin-top: 1rem;
}

.error-ref {
  font-size: 12px;
  color: #856404;
  margin-top: 4px;
}

.table td {
  vertical-align: middle;
}

.pagination-wrapper {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pagination-info {
  color: #7a7a7a;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #f0ad4e;
  }
}

.dialog-content {
  padding: 20px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  .info-label {
    color: #666;
  }

  .info-value {
    font-weight: 500;
  }
}

hr {
  margin: 15px 0;
  border: none;
  border-top: 1px solid #eee;
}

.form-group {
  margin-bottom: 16px;

  .label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #333;
  }

  .required {
    color: #dc3545;
  }

  .textarea {
    width: 100%;
  }

  .help-text {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
  }
}

.file-upload {
  display: flex;
  align-items: center;
  gap: 12px;

  .file-name {
    color: #28a745;
    font-size: 14px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
